import apiClient from './apiClient.js'

const API_BASE_URL = import.meta?.env?.VITE_API_BASE_URL || '/api'

class FileService {
    /**
     * Upload a file to the gallery
     * @param {File} file - The file to upload
     * @param {Function} onProgress - Optional callback for upload progress (percent: number) => void
     * @returns {Promise} API response
     */
    async uploadFile(file, onProgress = null) {
        try {
            const formData = new FormData()
            formData.append('file', file)

            const response = await apiClient.post('/files/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 0, // Sin límite de tiempo
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        )
                        onProgress(percentCompleted)
                    }
                }
            })
            return response.data.file
        } catch (error) {
            console.error('Error al subir archivo:', error)
            throw error
        }
    }

    /**
     * Get file URL for display
     * @param {string} filename - The filename
     * @returns {string} Full URL to the file
     */
    getFileUrl(filename) {
        const currentUrlHostAndProtocol = window.location.origin
        return `${currentUrlHostAndProtocol}${API_BASE_URL}/files/serve/${filename}`
    }

    getRelativeFileUrl(filename) {
        return `${API_BASE_URL}/files/serve/${filename}`
    }

    getRelativeFileHTMLUrl(filename) {
        return `${API_BASE_URL}/files/serve/html/${filename}`
    }

    /**
     * Delete a file
     * @param {String} fileName - The file name to delete
     * @returns {Promise} API response
     */
    async deleteFile(fileName) {
        try {
            const response = await apiClient.delete(`/files/${fileName}`)
            return response.data
        } catch (error) {
            console.error(`Error al eliminar archivo ${fileName}:`, error)
            throw error
        }
    }

    /**
     * Get files list
     * @param {String} folder - Optional folder path
     * @param {String} filter - Optional filter (e.g. *.jpg)
     * @returns {Promise} API response with files array
     */
    async getFiles(folder = '', filter = '') {
        try {
            const response = await apiClient.get('/files', {
                params: { folder, filter }
            })
            return response.data
        } catch (error) {
            console.error('Error al obtener lista de archivos:', error)
            throw error
        }
    }


    /**
     * Get files by category (nueva funcionalidad offline)
     * @param {string} category - Image, video, etc.
     * @returns {Promise} Files array
     */
    async getFilesByCategory(category = null) {
        try {
            const files = await this.getFiles()
            if (!category) {
                // Agrupar por categoría
                const grouped = {}
                files.forEach(file => {
                    const ext = file.name.split('.').pop().toLowerCase()
                    const fileCategory = this._getFileCategory(ext)
                    if (!grouped[fileCategory]) grouped[fileCategory] = []
                    grouped[fileCategory].push(file)
                })
                return grouped
            }
            // Filtrar por categoría específica
            return files.filter(file => {
                const ext = file.name.split('.').pop().toLowerCase()
                return this._getFileCategory(ext) === category
            })
        } catch (error) {
            console.error('Error al obtener archivos por categoría:', error)
            throw error
        }
    }

    /**
     * Get files manifest (nueva funcionalidad offline)
     */
    async getFilesManifest() {
        try {
            const files = await this.getFiles()
            return files.map(file => ({
                url: this.getRelativeFileUrl(file.name),
                name: file.name,
                size: file.size || 0,
                mimeType: file.mimeType || 'application/octet-stream',
                category: this._getFileCategory(file.name.split('.').pop().toLowerCase())
            }))
        } catch (error) {
            console.error('Error al obtener manifiesto de archivos:', error)
            throw error
        }
    }

    /**
     * Get file statistics
     */
    async getFilesStats() {
        try {
            const files = await this.getFiles()
            const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0)
            const byCategory = {}

            files.forEach(file => {
                const ext = file.name.split('.').pop().toLowerCase()
                const category = this._getFileCategory(ext)
                byCategory[category] = (byCategory[category] || 0) + 1
            })

            return {
                total: files.length,
                totalSize,
                byCategory,
                totalSizeFormatted: this._formatFileSize(totalSize)
            }
        } catch (error) {
            console.error('Error al obtener estadísticas de archivos:', error)
            throw error
        }
    }

    // === MÉTODOS HELPER ===

    /**
     * Determinar categoría de archivo por extensión
     */
    _getFileCategory(extension) {
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp']
        const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm']

        if (imageExts.includes(extension)) return 'image'
        if (videoExts.includes(extension)) return 'video'
        return 'other'
    }

    /**
     * Formatear tamaño de archivo
     */
    _formatFileSize(bytes) {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    /**
     * Check if file is an image
     * @param {string} filename - The filename
     * @returns {boolean} True if file is an image
     */
    isImage(filename) {
        if (!filename || typeof filename !== 'string') return false
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
        const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'))
        return imageExtensions.includes(extension)
    }

    /**
     * Check if file is a video
     * @param {string} filename - The filename
     * @returns {boolean} True if file is a video
     */
    isVideo(filename) {
        if (!filename || typeof filename !== 'string') return false
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.mkv']
        const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'))
        return videoExtensions.includes(extension)
    }

    /**
     * Format file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes'

        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
}

export default new FileService()
